import { h, defineComponent, ref, watch, VNodeChildren } from 'vue';
import { d, t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import * as q from '@/ui/routes/query';
import { box, level, loader, namedLink, pageTitle, pagination, sortableTextColumn, textColumn } from '@/ui/components';
import { inspectionList, inspectionListExportUrl, SupportedExportFormat } from '@/services/api/resources/analytics';
import { AnalyticsEntityGroup, emptyDetailList, Inspection, InspectionList } from '@/entities/analytics';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { AnalyticsTable } from '@/ui/components/table/AnalyticsTable';
import { SortDirection } from '@/ui/types';
import { InspectionGroupBy } from '@/services/api/resources/analytics/types';
import { download } from '@/ui/helpers/uiHelper';
import { exportDropdown } from '@/ui/components/dropdown/ExportDropdown';
import { renderForbiddenPage } from '../../error/ForbiddenPage';
import { useQueryFiltering } from './useQueryFilters';
import { inspectionDetailPageLink } from '@/ui/helpers/inspectionHelper';
import { renderAnalyticsTeaserPage } from '../../teasers/AnalyticsTeaserPage';
import { BackButton } from '@/ui/components/button/BackButton';

export const OverdueInspectionsDetailPage = defineComponent({
  name: 'OverdueInspectionsDetailPage',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('conductedAt', SortDirection.Descending);
    const {
      start, end, group, entityItem, assignee, category, checklist, response, section, questionTitle,
    } = useQueryFiltering();

    // get list task
    const query = () => ({
      timezone: user.value.timezone,
      page: page.value,
      perPage: perPage.value,
      sort: sort.value,
      dir: dir.value,
      start: start.value,
      end: end.value,
      entityItem: entityItem.value,
      group: group.value,
      assignee: assignee.value,
      category: category.value,
      checklist: checklist.value,
      response: response.value,
      section: section.value,
      questionTitle: questionTitle.value,
      overdue: true,
    });
    const list = ref<InspectionList>(emptyDetailList);
    const getList = useTask(
      () => inspectionList(query()),
      userList => {
        list.value = userList;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, group, entityItem, assignee, category, checklist, sort, dir], getList.run);

    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => inspectionListExportUrl(
        query(),
        format as SupportedExportFormat,
        user.value.locale,
      ),
      download,
    );

    return function render() {
      return h('div', [
        h('div', [
          h('div', [
            level(
              [h(BackButton), pageTitle(t('analytics.overdue-inspections'))],
              [exportDropdown(exportData)],
            ),
            box([
              loader('div', getList,
                [
                  h(AnalyticsTable, {
                    props: {
                      list: list.value.data,
                      sort: sort.value,
                      dir: dir.value,
                    },
                    scopedSlots: {
                      default: () => [
                        sortableTextColumn({
                          field: 'conductedAt',
                          label: t('checklist.conducted-at'),
                        }, {
                          default: ({ row }: { row: Inspection }) =>
                            row.data.conductedAt ? d(row.data.conductedAt) : '',
                        }),
                        sortableTextColumn({
                          field: 'dueAt',
                          label: t('common.deadline'),
                        }, {
                          default: ({ row }: { row: Inspection }) =>
                            row.data.dueAt ? d(row.data.dueAt) : '',
                        }),
                        textColumn({
                          field: 'checklistTitle',
                          label: t('checklist.title'),
                        }, {
                          default: ({ row }: { row: Inspection }) => row.authorizedTo.view
                            ? [inspectionDetailPageLink(row.data.checklistTitle, row.id)]
                            : row.data.checklistTitle,
                        }),
                        textColumn({
                          field: 'entityItem',
                          label: t('common.entity-item'),
                        }, {
                          default: ({ row }: { row: Inspection }) =>
                            row.data.entityItem ? row.data.entityItem.data.title : '',
                        }),
                        textColumn({
                          field: 'conductedBy',
                          label: t('checklist.conducted-by'),
                        }, {
                          default: ({ row }: { row: Inspection }) =>
                            row.data.conductedBy ? row.data.conductedBy.data.name : '',
                        }),
                      ],
                    },
                    on: {
                      sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                        resetPagination();
                        sort.value = field;
                        dir.value = direction;
                      }),
                    },
                  }),
                  (list.value.meta.total)
                    ? pagination({
                      current: page.value,
                      total: list.value.meta.total,
                      perPage: perPage.value,
                    })
                    : null,
                ], !initialLoading.value),
            ]),
          ]),
        ]),
      ]);
    };
  },
});

interface OverdueInspectionsDetailPageQuery {
  start: Date;
  end: Date;
  entityItem: number[];
  group: number[];
  assignee: number[];
  category: number[];
  checklist: number[];
}

export const overdueInspectionsDetailPageLink = (
  anchor: VNodeChildren,
  groupBy: InspectionGroupBy,
  group: AnalyticsEntityGroup,
  query: OverdueInspectionsDetailPageQuery,
) => namedLink(
  anchor,
  'analyticsOverdueInspectionsExplain',
  undefined,
  {
    start: q.dateEncoder(group.start || query.start),
    end: q.dateEncoder(group.end || query.end),
    entityItem: groupBy === InspectionGroupBy.EntityItem
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.entityItem),
    group: groupBy === InspectionGroupBy.Group
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.group),
    assignee: groupBy === InspectionGroupBy.Assignee
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.assignee),
    category: groupBy === InspectionGroupBy.Category
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.category),
    checklist: groupBy === InspectionGroupBy.Checklist
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.checklist),
  },
);
